import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import ManifestTimelineHelper from '../services/ManifestTimelineHelper';

export class HrtempLayer extends BaseProduct {
  public readonly id: ProductId = 'hrtemp';
  private zoomListener: (() => void) | null = null;
  private lastForcePmtiles: boolean | null = null;

  protected onActivate(): void {
    this.lastForcePmtiles = null;
    if (!this.zoomListener) {
      this.zoomListener = () => {
        if (!this.active) return;
        const state = store.getState();
        if (state.forecastAnimationPlaying) return;
        const minZoom = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.forecast_animation?.hrtemp_high_res_min_zoom) || 5.5;
        const shouldForce = this.map.getZoom() >= minZoom;
        if (this.lastForcePmtiles !== null && shouldForce !== this.lastForcePmtiles) {
          this.refresh();
        }
      };
      this.map.on('zoomend', this.zoomListener);
    }
    this.refresh();
  }

  protected onDeactivate(): void {
    if (this.zoomListener) {
      this.map.off('zoomend', this.zoomListener);
      this.zoomListener = null;
    }
    this.lastForcePmtiles = null;
    this.layerManager.removeBufferedRasterLayer(this.id);
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh(targetTimeMs?: number) {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    // Support pure contours mode by removing base temperature raster when disabled
    const isRasterEnabled = (state as any).hrtempRasterEnabled !== false;
    if (!isRasterEnabled) {
      this.layerManager.removeBufferedRasterLayer(this.id);
      return;
    }

    const opacity = getLayerOpacity(this.id, 1.0);
    const current = targetTimeMs !== undefined ? targetTimeMs : state.currentTimeStep;
    if (!current) return;

    const selectedModel = state.selectedModels[this.id];
    let model = (selectedModel && selectedModel !== 'rtma') ? selectedModel : (manifest.default_models?.['hrtemp'] || 'skysk_hres_topo');
    let frames = ManifestTimelineHelper.getProductFrames(manifest, 'hrtemp', model);

    if (frames.length === 0 && !selectedModel) {
      const avail = ManifestTimelineHelper.getAvailableModelsForProduct(manifest, 'hrtemp');
      for (const mObj of avail) {
        if (mObj.id === 'rtma') continue;
        const f = ManifestTimelineHelper.getProductFrames(manifest, 'hrtemp', mObj.id);
        if (f.length > 0) {
          model = mObj.id;
          frames = f;
          break;
        }
      }
    }

    if (frames.length === 0) {
      this.layerManager.removeBufferedRasterLayer(this.id);
      return;
    }

    const onDiskModel = manifest?.models_config?.[model]?.name_on_disk || model;
    const isHighResTopo = onDiskModel === 'skysk_hres_topo' || model === 'skysk_hres_topo' || model === 'nbm_highres';
    const maxZoom = isHighResTopo ? 8 : 5;
    const highResMinZoom = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.forecast_animation?.hrtemp_high_res_min_zoom) || 5.5;
    const isAnimating = Boolean(state.forecastAnimationPlaying || targetTimeMs !== undefined);
    const forcePmtiles = isHighResTopo && !isAnimating && this.map.getZoom() >= highResMinZoom;
    this.lastForcePmtiles = forcePmtiles;

    this.updateStandardInterpolatedRaster({
      layerId: this.id,
      product: this.id,
      model,
      targetTime: current,
      opacity,
      minzoom: 0,
      maxzoom: maxZoom,
      forcePmtiles
    });
  }
}
export default HrtempLayer;
